import type { CellContext } from '@tanstack/react-table';

import {
  createColumnConfig,
  createDateColumn,
  createFilterField,
  createTextColumn,
  useGenericListConfig,
} from '@pesto/ui';
import { Badge, type BadgeProps } from '@pesto/ui/components/Badge';
import { Button } from '@pesto/ui/components/Button';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import { Typography } from '@pesto/ui/components/Typography';
import { Eye } from 'lucide-react';

import type { RecentJobsResponseJobsItem } from '../../../__generated__/rest/api';

import { RecentJobsResponseJobsItemState } from '../../../__generated__/rest/api';

export const JOB_STATE_LABELS: Record<RecentJobsResponseJobsItemState, string> = {
  [RecentJobsResponseJobsItemState.available]: 'Available',
  [RecentJobsResponseJobsItemState.executing]: 'Executing',
  [RecentJobsResponseJobsItemState.completed]: 'Completed',
  [RecentJobsResponseJobsItemState.retryable]: 'Retryable',
  [RecentJobsResponseJobsItemState.discarded]: 'Discarded',
  [RecentJobsResponseJobsItemState.cancelled]: 'Cancelled',
};

export const JOB_STATE_VARIANTS: Record<RecentJobsResponseJobsItemState, BadgeProps['variant']> = {
  [RecentJobsResponseJobsItemState.available]: 'secondary',
  [RecentJobsResponseJobsItemState.executing]: 'default',
  [RecentJobsResponseJobsItemState.completed]: 'success',
  [RecentJobsResponseJobsItemState.retryable]: 'warning',
  [RecentJobsResponseJobsItemState.discarded]: 'destructive',
  [RecentJobsResponseJobsItemState.cancelled]: 'destructive',
};

export const formatJobArgs = (args: RecentJobsResponseJobsItem['args']) => {
  if (!args || Object.keys(args).length === 0) {
    return null;
  }

  try {
    return JSON.stringify(args, null, 2);
  } catch (error) {
    console.warn('Failed to stringify job args', error);

    return null;
  }
};

interface UseObanJobsListConfigOptions {
  onViewJob?: (job: RecentJobsResponseJobsItem) => void;
}

export const useObanJobsListConfig = ({ onViewJob }: UseObanJobsListConfigOptions = {}) => {
  const filterFields = [
    createFilterField('Queue', 'queue', { placeholder: 'Queue contains…' }),
    createFilterField('State', 'state', {
      options: Object.values(RecentJobsResponseJobsItemState).map((state) => ({
        label: JOB_STATE_LABELS[state],
        value: state,
      })),
    }),
  ];

  const columns = [
    createColumnConfig('id', ({ column }) => <DataTableColumnHeader column={column} title="Job ID" />, {
      accessorFn: (row: RecentJobsResponseJobsItem) => row.id,
      cellType: 'text',
      enableSorting: true,
    }),
    createColumnConfig('worker', ({ column }) => <DataTableColumnHeader column={column} title="Worker" />, {
      accessorFn: (row: RecentJobsResponseJobsItem) => row.worker ?? '—',
      enableSorting: true,
      cellType: 'text',
    }),
    createTextColumn(
      'queue',
      ({ column }) => <DataTableColumnHeader column={column} title="Queue" />,
      (row: RecentJobsResponseJobsItem) => row.queue,
      {
        enableSorting: true,
      },
    ),
    createColumnConfig('state', ({ column }) => <DataTableColumnHeader column={column} title="State" />, {
      accessorFn: (row: RecentJobsResponseJobsItem) => row.state,
      enableSorting: true,
      cell: ({ row }: CellContext<RecentJobsResponseJobsItem, unknown>) => {
        const job = row.original;
        const variant = JOB_STATE_VARIANTS[job.state] ?? 'secondary';

        return (
          <Badge variant={variant} className="capitalize">
            {JOB_STATE_LABELS[job.state] ?? job.state}
          </Badge>
        );
      },
    }),
    createColumnConfig('attempt', ({ column }) => <DataTableColumnHeader column={column} title="Attempt" />, {
      accessorFn: (row: RecentJobsResponseJobsItem) => row.attempt,
      enableSorting: true,
      cell: ({ row }: CellContext<RecentJobsResponseJobsItem, unknown>) => {
        const job = row.original;

        return (
          <Typography variant="body" className="text-xs">
            {job.attempt} / {job.attempts}
          </Typography>
        );
      },
    }),
    createColumnConfig('errors', ({ column }) => <DataTableColumnHeader column={column} title="Errors" />, {
      accessorFn: (row: RecentJobsResponseJobsItem) => row.errors?.join(' '),
      enableSorting: false,
      cell: ({ row }: CellContext<RecentJobsResponseJobsItem, unknown>) => {
        const job = row.original;
        const amountOfErrors = job.errors?.length;

        if (!amountOfErrors) {
          return (
            <Typography variant="body" className="text-muted-foreground text-xs">
              None
            </Typography>
          );
        }

        return (
          <div className="bg-destructive/10 flex flex-col items-center gap-1 rounded px-2 py-1">
            <Typography variant="body" className="text-xs">
              {amountOfErrors}
            </Typography>
          </div>
        );
      },
    }),
    createDateColumn(
      'insertedAt',
      ({ column }) => <DataTableColumnHeader column={column} title="Inserted" />,
      (row: RecentJobsResponseJobsItem) => row.insertedAt,
      {
        enableSorting: true,
      },
    ),
    createDateColumn(
      'scheduledAt',
      ({ column }) => <DataTableColumnHeader column={column} title="Scheduled" />,
      (row: RecentJobsResponseJobsItem) => row.scheduledAt,
      {
        enableSorting: true,
      },
    ),
    createDateColumn(
      'attemptedAt',
      ({ column }) => <DataTableColumnHeader column={column} title="Attempted" />,
      (row: RecentJobsResponseJobsItem) => row.attemptedAt,
      {
        enableSorting: true,
      },
    ),
    createDateColumn(
      'completedAt',
      ({ column }) => <DataTableColumnHeader column={column} title="Completed" />,
      (row: RecentJobsResponseJobsItem) => row.completedAt,
      {
        enableSorting: true,
      },
    ),

    createColumnConfig('args', ({ column }) => <DataTableColumnHeader column={column} title="Args" />, {
      accessorFn: (row: RecentJobsResponseJobsItem) => formatJobArgs(row.args) ?? '',
      enableSorting: false,
      cell: ({ row }: CellContext<RecentJobsResponseJobsItem, unknown>) => {
        const job = row.original;
        const formatted = formatJobArgs(job.args);

        if (!formatted) {
          return (
            <Typography variant="body" className="text-muted-foreground text-xs">
              None
            </Typography>
          );
        }

        return (
          <pre className="bg-muted max-h-32 overflow-auto whitespace-pre-wrap rounded px-2 py-1 text-xs">
            {formatted}
          </pre>
        );
      },
    }),
  ];

  if (onViewJob) {
    columns.unshift(
      createColumnConfig('view', 'View', {
        enableSorting: false,
        enablePinning: true,
        size: 90,
        cell: ({ row }: CellContext<RecentJobsResponseJobsItem, unknown>) => {
          const job = row.original;

          return (
            <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => onViewJob(job)}>
              <Eye className="h-4 w-4" />
              Details
            </Button>
          );
        },
      }),
    );
  }

  return useGenericListConfig<RecentJobsResponseJobsItem>({
    columns,
    filterFields,
    showActionColumn: false,
    defaultSorting: [{ id: 'insertedAt', desc: true }],
    columnVisibility: {
      args: false,
    },
    pinnedColumns: onViewJob ? ['view'] : [],
  });
};
